import type { FieldValues, UseFormReturn } from "react-hook-form";
import {
  Accordion,
  AccordionItem,
  AccordionTrigger,
  AccordionContent,
} from "@apac/components/ui";
import { TextField, type TextFieldProps } from "@apac/form-ui";
import {
  APAC_TRAVELERS_NAME,
  ApacTravelersComponent,
  useAssistanceSelection,
} from "@apac/travelers";
import {
  Select,
  SelectArrow,
  SelectItem,
  SelectItemCheck,
  SelectPopover,
  SelectProvider,
} from "@ariakit/react/select";
import {
  CoreIconNameEnum,
  useModuleBEM,
  useStyles,
} from "@b2bportal/core-themes";
import { useI18nContext } from "@hopper-b2b/i18n";
import {
  DialogContent,
  Dialog,
  DialogTrigger,
  DialogClose,
  Icon,
} from "@tenant/ui";
import defaultStyles from "./styles.module.scss";

export type AdditionalInfoFieldOverrides = {
  redressNumber?: Partial<TextFieldProps<FieldValues>>;
  travellerNumber?: Partial<TextFieldProps<FieldValues>>;
};

interface AdditionalInfoProps {
  formContext: UseFormReturn<FieldValues, unknown, undefined>;
  fieldOverrides: AdditionalInfoFieldOverrides;
}

export const AdditionalInfo: React.FC<AdditionalInfoProps> = ({
  formContext,
  fieldOverrides: { redressNumber = {}, travellerNumber = {} },
}) => {
  const styles = useStyles(
    APAC_TRAVELERS_NAME,
    ApacTravelersComponent.AdditionalInfo,
    defaultStyles
  );
  const [, cn] = useModuleBEM(styles, ApacTravelersComponent.AdditionalInfo);
  const { t } = useI18nContext();

  const {
    context: {
      label,
      assistanceOptions,
      selectedAssistanceOptions,
      redressModalOpen,
      ktnModalOpen,
    },
    handlers: {
      isOptionSelected,
      onSelectedAssistanceOptionsChange,
      setRedressModalOpen,
      setKtnModalOpen,
    },
  } = useAssistanceSelection({ formContext });

  return (
    <Accordion defaultValue="additional-info-details">
      <AccordionItem value="additional-info-details">
        <AccordionTrigger className={cn("trigger")}>
          <div className={cn("title")}>
            {t("travelerForm.additionalInfo.title")}
          </div>
        </AccordionTrigger>

        <AccordionContent>
          <div className={cn("container")}>
            <div className={cn("assistance-container")}>
              <Dialog>
                <DialogTrigger className={cn("assistance-label-container")}>
                  <span className={cn("assistance-label")}>
                    {t("travelerForm.additionalInfo.assistanceRequiredLabel")}
                  </span>
                  <Icon iconName={CoreIconNameEnum.info} />
                </DialogTrigger>
                <DialogContent
                  title={t("travelerForm.additionalInfo.selectAssistanceLabel")}
                >
                  <div className={cn("additional-info-dialog-container")}>
                    <span className={cn("additional-info-dialog")}>
                      {t("travelerForm.additionalInfo.assistanceRequiredInfo")}
                    </span>
                    <DialogClose className={cn("close-button")}>
                      {t("close.button")}
                    </DialogClose>
                  </div>
                </DialogContent>
              </Dialog>
              <SelectProvider
                value={selectedAssistanceOptions}
                setValue={(v) => {
                  onSelectedAssistanceOptionsChange(v);
                }}
              >
                <Select className={cn("assistance-select-trigger")}>
                  <span
                    className={cn("assistance-select-trigger-label", {
                      placeholder: selectedAssistanceOptions.length === 0,
                    })}
                  >
                    {selectedAssistanceOptions.length > 0
                      ? label
                      : t("travelerForm.additionalInfo.selectAssistanceLabel")}
                  </span>
                  <SelectArrow />
                </Select>
                <SelectPopover
                  gutter={4}
                  sameWidth
                  unmountOnHide
                  className={cn("assistance-popover")}
                >
                  {assistanceOptions.map((option) => (
                    <SelectItem
                      key={option.value}
                      value={option.value}
                      className={cn("assistance-option")}
                    >
                      {/* Can't use core/Checkbox otherwise endless select provider setValue event */}
                      <SelectItemCheck
                        className={cn("assistance-option-checkbox", {
                          checked: isOptionSelected(option.value),
                        })}
                      />
                      <span className={cn("option-label")}>{option.label}</span>
                    </SelectItem>
                  ))}
                </SelectPopover>
              </SelectProvider>
            </div>

            <TextField
              formContext={formContext}
              name={"redressNumber"}
              label={t("redressNumber")}
              placeholder={t("redressNumber")}
              labelIconName={CoreIconNameEnum.info}
              onLabelClick={() => setRedressModalOpen(true)}
              {...redressNumber}
            />
            <Dialog
              open={redressModalOpen}
              onOpenChange={(open) => setRedressModalOpen(open)}
            >
              <DialogContent title={t("redressNumber")}>
                <div className={cn("additional-info-dialog-container")}>
                  <span className={cn("additional-info-dialog")}>
                    {t("travelerForm.additionalInfo.redressNumberInfo")}
                  </span>
                  <DialogClose className={cn("close-button")}>
                    {t("close.button")}
                  </DialogClose>
                </div>
              </DialogContent>
            </Dialog>

            <TextField
              formContext={formContext}
              name={"travellerNumber"}
              label={t("travelerForm.additionalInfo.knownTravelerNumberLabel")}
              placeholder={t(
                "travelerForm.additionalInfo.knownTravelerNumberLabel"
              )}
              labelIconName={CoreIconNameEnum.info}
              onLabelClick={() => setKtnModalOpen(true)}
              {...travellerNumber}
            />
            <Dialog
              open={ktnModalOpen}
              onOpenChange={(open) => setKtnModalOpen(open)}
            >
              <DialogContent
                title={t(
                  "travelerForm.additionalInfo.knownTravelerNumberLabel"
                )}
              >
                <div className={cn("additional-info-dialog-container")}>
                  <span className={cn("additional-info-dialog")}>
                    {t("travelerForm.additionalInfo.travelerNumberInfo")}
                  </span>
                  <DialogClose className={cn("close-button")}>
                    {t("close.button")}
                  </DialogClose>
                </div>
              </DialogContent>
            </Dialog>
          </div>
        </AccordionContent>
      </AccordionItem>
    </Accordion>
  );
};
